/**
 * Rebuild FormData / File bodies from persisted request.body JSON for AI Agent execution.
 * Mirrors Collections form-data / binary hydration so uploads assert the same as Send.
 */

const FORM_DATA_FILE_BLOB_STORAGE_PREFIX = 'dft_form_data_files_';

type StoredFileBlob = {
  base64?: string;
  fileName?: string;
  mimeType?: string;
};

function storedBlobToFile(parsed: StoredFileBlob | null | undefined): File | null {
  if (!parsed?.base64 || !parsed.fileName) return null;
  try {
    const bin = atob(parsed.base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], parsed.fileName, {
      type: parsed.mimeType || 'application/octet-stream',
    });
  } catch {
    return null;
  }
}

function normalizeBodyType(bodyType?: string | null): string {
  return String(bodyType ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

function loadSessionFormDataFiles(requestId?: number | null): Record<string, File | null> {
  if (requestId == null) return {};
  try {
    const raw = sessionStorage.getItem(`${FORM_DATA_FILE_BLOB_STORAGE_PREFIX}${requestId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { files?: Record<string, StoredFileBlob> };
    const result: Record<string, File | null> = {};
    for (const [cacheKey, blob] of Object.entries(parsed.files ?? {})) {
      const file = storedBlobToFile(blob);
      if (file) result[cacheKey] = file;
    }
    return result;
  } catch {
    return {};
  }
}

function resolveFormDataRowFile(
  row: { key?: unknown; value?: unknown; type?: string; __fileBlob?: StoredFileBlob },
  idx: number,
  fileCache: Record<string, File | null>,
): File | null {
  if (row?.type !== 'file') return null;
  const fromBlob = storedBlobToFile(row.__fileBlob);
  if (fromBlob) return fromBlob;
  const keyForCache = `${String(row.key ?? '').trim()}::${String(row.value ?? '').trim()}`;
  return fileCache[`idx:${idx}`] ?? (keyForCache.trim() ? fileCache[keyForCache] : null) ?? null;
}

function looksLikeFormDataRows(raw: string): boolean {
  try {
    const parsed = JSON.parse(raw);
    return (
      Array.isArray(parsed) &&
      parsed.some(
        (row) =>
          row &&
          typeof row === 'object' &&
          (row.type === 'file' || row.type === 'text' || 'key' in row),
      )
    );
  } catch {
    return false;
  }
}

function looksLikeBinaryMeta(raw: string): boolean {
  try {
    const parsed = JSON.parse(raw);
    return (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Boolean((parsed as { __fileBlob?: unknown }).__fileBlob)
    );
  } catch {
    return false;
  }
}

function buildFormDataFromStoredBody(
  raw: string,
  fileCache: Record<string, File | null> = {},
): FormData | null {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const formData = new FormData();
    let appended = 0;
    parsed.forEach(
      (
        row: {
          key?: unknown;
          value?: unknown;
          type?: string;
          enabled?: boolean;
          __fileBlob?: StoredFileBlob;
        },
        idx: number,
      ) => {
        if (row?.enabled === false) return;
        if (row?.type === 'file') {
          const file = resolveFormDataRowFile(row, idx, fileCache);
          if (!file) return;
          const formKey = String(row.key ?? '').trim() || `file_${idx}`;
          formData.append(formKey, file, file.name || 'upload');
          appended++;
          return;
        }
        const key = String(row?.key ?? '').trim();
        if (!key) return;
        formData.append(key, String(row?.value ?? ''));
        appended++;
      },
    );
    return appended > 0 ? formData : null;
  } catch {
    return null;
  }
}

function buildBinaryFileFromStoredBody(raw: string): File | null {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return storedBlobToFile((parsed as { __fileBlob?: StoredFileBlob }).__fileBlob);
  } catch {
    return null;
  }
}

export type AgentPreparedUploadBody = {
  body: FormData | File | string;
  headers: Record<string, string>;
  /** True when body was rebuilt as multipart/binary (not raw JSON storage). */
  rebuilt: boolean;
};

/**
 * Convert prepared agent body + bodyType into a proxy-ready upload body.
 * Strips Content-Type for multipart so the browser/proxy sets the boundary.
 */
export function prepareAgentUploadBodyForProxy(args: {
  body?: string | null;
  bodyType?: string | null;
  headers?: Record<string, string> | null;
  requestId?: number | null;
}): AgentPreparedUploadBody {
  const headers: Record<string, string> = { ...(args.headers ?? {}) };
  const raw = typeof args.body === 'string' ? args.body : '';
  const bodyType = normalizeBodyType(args.bodyType);
  const fileCache = loadSessionFormDataFiles(args.requestId);

  const isFormData =
    bodyType === 'form-data' ||
    bodyType === 'multipart/form-data' ||
    bodyType === 'form data' ||
    looksLikeFormDataRows(raw);
  const isBinary = bodyType === 'binary' || (!isFormData && looksLikeBinaryMeta(raw));

  const stripContentType = () => {
    Object.keys(headers).forEach((key) => {
      if (key.toLowerCase() === 'content-type') delete headers[key];
    });
  };

  if (raw && isFormData) {
    const formData = buildFormDataFromStoredBody(raw, fileCache);
    if (formData) {
      stripContentType();
      return { body: formData, headers, rebuilt: true };
    }
    stripContentType();
  }

  if (raw && isBinary) {
    const file = buildBinaryFileFromStoredBody(raw);
    if (file) {
      stripContentType();
      headers['Content-Type'] = file.type?.trim() || 'application/octet-stream';
      return { body: file, headers, rebuilt: true };
    }
    stripContentType();
  }

  return { body: raw, headers, rebuilt: false };
}
